import { skipToken } from '@apollo/client'
import { differenceInMinutes, format } from 'date-fns'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { type ReactNode, use, useCallback, useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceArea,
  XAxis,
  YAxis,
} from 'recharts'
import { ChartTimeframeProviderContext } from '@/components/shared/analytics/chart-timeframe/context/context'
import { useChartZoom } from '@/components/shared/analytics/hooks/use-chart-zoom'
import { useLogsExplorer } from '@/components/shared/logs-explorer/hooks/use-logs-explorer'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/sv/chart'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { getXAxisDateFormat } from '@/config/charts'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useIdProject, useSuspenseQuery } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  LogStatusCodeChartQuery,
  LogStatusCodeChartQueryVariables,
} from '@/components/shared/logs-explorer/components/log-status-code-chart.types.gen'
import type { LogService, MetricsFilterInput } from '@/graphql.types'

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPod|iPhone|iPad/.test(navigator.platform)

const LOG_STATUS_CODE_CHART_QUERY = gql`
  query LogStatusCodeChart(
    $idCompany: String!
    $idProject: String
    $idService: String!
    $serviceType: LogService!
    $timeframe: MetricsTimeFrameInput!
    $intervalInSeconds: Int
    $filters: [MetricsFilterInput!]
  ) {
    company(id: $idCompany) {
      id
      cloudflareMetrics(
        idService: $idService
        idProject: $idProject
        serviceType: $serviceType
        timeframe: $timeframe
        intervalInSeconds: $intervalInSeconds
        filters: $filters
      ) {
        idCompany
        timeframe {
          start
          end
        }
        statusCodes {
          time
          value
        }
      }
    }
  }
`

type Props = {
  idService: string
  serviceType: LogService
  filters?: MetricsFilterInput[]
}

const chartConfig = {
  '2xx': { label: '2xx', color: 'oklch(0.685 0.169 237.323)' },
  '3xx': { label: '3xx', color: 'oklch(0.551 0.027 264.364)' },
  '4xx': { label: '4xx', color: 'oklch(0.769 0.188 70.08)' },
  '5xx': { label: '5xx', color: 'var(--destructive)' },
} satisfies ChartConfig

const SummaryBadge = ({
  color,
  label,
  value,
}: {
  color: string
  label: string
  value: number
}) => (
  <Badge variant="outline" className="p-0 px-2">
    <div className={cn('h-2.5 w-2.5 rounded-xs', color)} /> {label}
    <Separator orientation="vertical" className="h-5!" />{' '}
    <div className="font-bold">
      {value > 1000 ? `${Math.round(value / 100) / 10}k` : value}
    </div>
  </Badge>
)

export const LogStatusCodeChart = ({
  idService,
  serviceType,
  filters,
}: Props) => {
  const { idCompany } = useIdCompany()
  const { idProject } = useIdProject()
  const { timeframe } = use(ChartTimeframeProviderContext)
  const { isChartCollapsed, setIsChartCollapsed } = useLogsExplorer()

  const minutesDiff = differenceInMinutes(
    timeframe.endDate ?? 0,
    timeframe.startDate ?? 0
  )

  const { data } = useSuspenseQuery<
    LogStatusCodeChartQuery,
    LogStatusCodeChartQueryVariables
  >(
    LOG_STATUS_CODE_CHART_QUERY,
    !timeframe?.startDate || !timeframe?.endDate
      ? skipToken
      : {
          variables: {
            idCompany,
            idProject,
            idService,
            serviceType,
            intervalInSeconds: minutesDiff ?? 90,
            timeframe: {
              start: timeframe.startDate.toISOString(),
              end: timeframe.endDate.toISOString(),
            },
            filters: filters?.length ? filters : undefined,
          },
        }
  )

  const { chartData, summary } = useMemo(() => {
    const statusCodes = data?.company?.cloudflareMetrics?.statusCodes ?? []
    const totals = { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0 }

    const points = statusCodes.map((item) => {
      const value = item?.value || {}
      totals['2xx'] += Number(value['2xx'] || 0)
      totals['3xx'] += Number(value['3xx'] || 0)
      totals['4xx'] += Number(value['4xx'] || 0)
      totals['5xx'] += Number(value['5xx'] || 0)

      return {
        time: Number(item?.time),
        '2xx': Number(value['2xx'] || 0),
        '3xx': Number(value['3xx'] || 0),
        '4xx': Number(value['4xx'] || 0),
        '5xx': Number(value['5xx'] || 0),
      }
    })

    return { chartData: points, summary: totals }
  }, [data?.company?.cloudflareMetrics?.statusCodes])

  const intervalMs = (minutesDiff ?? 90) * 1000

  const { chartProps, referenceAreaProps, isSelecting } = useChartZoom({
    intervalMs,
  })

  const tooltipLabelFormatter = useCallback(
    (
      _label: ReactNode,
      items: ReadonlyArray<{ payload?: { time?: number } }>
    ) => {
      const t = items?.[0]?.payload?.time
      return t ? format(t, 'MMM dd, yyyy HH:mm') : ''
    },
    []
  )

  const tooltipValueFormatter = useCallback((value: unknown) => {
    const numValue = Number(value)
    return numValue > 1000
      ? `${Math.round(numValue / 100) / 10}k`
      : String(value)
  }, [])

  if (chartData.length === 0) {
    return null
  }

  return (
    <Collapsible
      open={!isChartCollapsed}
      onOpenChange={(open) => setIsChartCollapsed(!open)}
      className="border-b"
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            {isChartCollapsed ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            Status codes
            <kbd className="ml-1.5 inline-flex h-5 items-center rounded border bg-muted px-1 font-mono text-[12px] text-muted-foreground">
              {isMac ? '⌘' : 'Ctrl'} H
            </kbd>
          </CollapsibleTrigger>
          <div className="flex items-center gap-2 font-mono">
            <SummaryBadge
              label="2xx"
              value={summary['2xx']}
              color="bg-sky-500"
            />
            <SummaryBadge
              label="3xx"
              value={summary['3xx']}
              color="bg-slate-500"
            />
            <SummaryBadge
              label="4xx"
              value={summary['4xx']}
              color="bg-amber-500"
            />
            <SummaryBadge
              label="5xx"
              value={summary['5xx']}
              color="bg-destructive"
            />
          </div>
        </div>
        <CollapsibleContent>
          <ChartContainer
            config={chartConfig}
            className="h-34 w-full select-none [&_.recharts-wrapper]:!cursor-crosshair mt-2"
          >
            <BarChart accessibilityLayer data={chartData} {...chartProps}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="time"
                scale="time"
                type="number"
                domain={[
                  timeframe?.startDate ? timeframe?.startDate?.getTime() : 0,
                  timeframe?.endDate
                    ? timeframe?.endDate.getTime()
                    : Date.now(),
                ]}
                tickLine={false}
                interval="equidistantPreserveStart"
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) =>
                  value &&
                  format(
                    value,
                    getXAxisDateFormat({
                      startDate: timeframe.startDate,
                      endDate: timeframe.endDate,
                      intervalInSeconds: timeframe.intervalInSeconds,
                    })
                  )
                }
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickCount={4}
                tickFormatter={(val) => `${Math.round(Number(val ?? '0'))}`}
                allowDecimals={false}
              />
              <ChartTooltip
                cursor={{
                  stroke: 'var(--foreground)',
                  strokeDasharray: '4 4',
                  strokeWidth: 1,
                  opacity: 0.5,
                }}
                wrapperStyle={
                  isSelecting ? { visibility: 'hidden' } : undefined
                }
                content={
                  <ChartTooltipContent
                    indicator="dot"
                    hideLabel={false}
                    labelFormatter={tooltipLabelFormatter}
                    valueFormatter={tooltipValueFormatter}
                  />
                }
              />
              <Bar dataKey="2xx" stackId="a" fill={chartConfig['2xx'].color} />
              <Bar dataKey="3xx" stackId="a" fill={chartConfig['3xx'].color} />
              <Bar dataKey="4xx" stackId="a" fill={chartConfig['4xx'].color} />
              <Bar dataKey="5xx" stackId="a" fill={chartConfig['5xx'].color} />
              {referenceAreaProps && <ReferenceArea {...referenceAreaProps} />}
            </BarChart>
          </ChartContainer>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export const LogStatusCodeChartFallback = () => (
  <div className="px-4 py-3 border-b">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <Skeleton className="size-4" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-12" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-14" />
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-16" />
      </div>
    </div>
    <Skeleton className="h-34 w-full mt-1 mb-0.5" />
  </div>
)
